import { useTranslation } from 'react-i18next';
import { AlertTriangle, Clock } from 'lucide-react';
import { differenceInDays, format } from 'date-fns';
import { he } from 'date-fns/locale';
import { getDeadlineStatus } from './DeadlineBadge';

interface DeadlineBannerProps {
  dueDate?: string | null;
  source?: 'ticket' | 'milestone' | 'project' | null;
  warningDays?: number;
  entityLabel?: string;
}

const sourceKeys: Record<string, string> = {
  ticket: 'deadline.theTicket',
  milestone: 'deadline.theMilestone',
  project: 'deadline.theProject',
};

export default function DeadlineBanner({ dueDate, source, warningDays = 7, entityLabel }: DeadlineBannerProps) {
  const { t } = useTranslation();
  if (!dueDate) return null;

  const status = getDeadlineStatus(dueDate, warningDays);
  if (status === 'ok') return null;

  const due = new Date(dueDate);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  const diffDays = differenceInDays(due, now);
  const dateStr = format(due, 'dd/MM/yyyy', { locale: he });
  const entity = entityLabel || (source ? t(sourceKeys[source]) : '');

  if (status === 'overdue') {
    return (
      <div className="w-full px-4 py-3 bg-danger-50 border border-danger-100 rounded-xl flex items-center gap-3 animate-pulse">
        <AlertTriangle size={20} className="text-danger-600 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-red-800">
            {entity ? `${entity}${t('deadline.isOverdue')}` : t('deadline.isOverdue').trim()}
          </p>
          <p className="text-xs text-danger-600">
            {t('deadline.dueDateLabel')}{dateStr}{t('deadline.daysOverdue', { days: Math.abs(diffDays) })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-3 bg-warning-50 border border-warning-200 rounded-xl flex items-center gap-3">
      <Clock size={20} className="text-warning-600 flex-shrink-0" />
      <div>
        <p className="text-sm font-semibold text-amber-800">
          {entity ? `${entity}${t('deadline.approachingDeadline')}` : t('deadline.approachingDeadline').trim()}
        </p>
        <p className="text-xs text-warning-600">
          {t('deadline.dueDateLabel')}{dateStr}{diffDays === 0 ? ` · ${t('deadline.overdueToday')}` : t('deadline.daysRemaining', { days: diffDays })}
        </p>
      </div>
    </div>
  );
}
